import { TextTooltip } from '@root/components'
import { FaCheck, FaChevronRight, FaExclamationCircle, FaQuestionCircle } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

interface Props {
  sectionId: number
  /** Visual step number; defaults to sectionId. */
  displayNumber?: number
  title: string
  subtitle: React.ReactNode
  summary: string
  open: boolean
  complete: boolean
  hasError: boolean
  onToggle: () => void
  children: React.ReactNode
}

const FormAccordionSection: React.FC<Props> = ({
  sectionId,
  displayNumber,
  title,
  subtitle,
  summary,
  open,
  complete,
  hasError,
  onToggle,
  children
}) => {
  const [t] = useTranslation()
  const contentId = `automation-rule-section-${sectionId}`
  const tone = hasError ? 'error' : !open && complete ? 'done' : 'default'
  const stepNumber = displayNumber ?? sectionId

  return (
    <Card $invalid={hasError}>
      <HeaderButton type="button" aria-expanded={open} aria-controls={contentId} onClick={onToggle}>
        <Chevron $open={open} />
        <NumberCircle $tone={tone}>{tone === 'done' ? <FaCheck /> : stepNumber}</NumberCircle>
        <TextColumn>
          <TitleRow>
            <Title>{title}</Title>
            {subtitle != null && subtitle !== '' && (
              <InfoTrigger
                onClick={event => event.stopPropagation()}
                onKeyDown={event => event.stopPropagation()}
              >
                <TextTooltip placement="bottom-start" maxWidth={360} tooltip={subtitle}>
                  <HelpIcon aria-label={typeof subtitle === 'string' ? subtitle : undefined} />
                </TextTooltip>
              </InfoTrigger>
            )}
          </TitleRow>
          {!open && summary ? <Summary>{summary}</Summary> : null}
        </TextColumn>
        {hasError && (
          <ErrorPill>
            <FaExclamationCircle />
            {t('settings.automation.accountingRules.form.missingInfo')}
          </ErrorPill>
        )}
      </HeaderButton>
      <Content id={contentId} $open={open} aria-hidden={!open}>
        {children}
      </Content>
    </Card>
  )
}

export default FormAccordionSection

const Card = styled.div<{ $invalid: boolean }>`
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid
    ${({ theme, $invalid }) => ($invalid ? theme.colors.error : theme.colors.borderSoft)};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
`

const HeaderButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  text-align: left;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 0;
  cursor: pointer;
  padding: 0.75rem 1rem;
  font-family: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
`

const Chevron = styled(FaChevronRight)<{ $open: boolean }>`
  width: 12px;
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? 'rotate(90deg)' : 'rotate(0deg)')};
`

const NumberCircle = styled.span<{ $tone: 'default' | 'error' | 'done' }>`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  background: ${({ theme, $tone }) =>
    $tone === 'done'
      ? theme.dashboard.positiveTint
      : $tone === 'error'
      ? theme.dashboard.criticalTint
      : theme.dashboard.infoTint};
  color: ${({ theme, $tone }) =>
    $tone === 'done'
      ? theme.dashboard.positive
      : $tone === 'error'
      ? theme.dashboard.critical
      : theme.dashboard.infoDeep};

  svg {
    width: 10px;
    height: 10px;
  }
`

const TextColumn = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
`

const TitleRow = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
`

const Title = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const InfoTrigger = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`

const HelpIcon = styled(FaQuestionCircle)`
  display: block;
  width: 0.85rem;
  height: 0.85rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const Summary = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
  white-space: normal;
  text-wrap: pretty;
`

const ErrorPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
  background: ${({ theme }) => theme.dashboard.criticalTint};
  color: ${({ theme }) => theme.dashboard.critical};
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  padding: 0.2rem 0.55rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  white-space: nowrap;

  svg {
    width: 10px;
    height: 10px;
  }
`

const Content = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: 0.25rem 1rem 1rem;
`
